import React, { useState } from "react";
import { SiWhatsapp as WhatsappIcon } from "@icons-pack/react-simple-icons";
import { Pencil, Trash2 } from "lucide-react";
import { Tooltip } from "@/components/atoms";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import "./Table.scss";

export interface TableColumn<T> {
  id: string;
  header: string;
  cell: (row: T, index: number) => React.ReactNode;
  className?: string;
}

export interface TableRowActions<T> {
  onEdit: (row: T, index: number) => void;
  onDelete: (row: T, index: number) => void;
  getWhatsAppHref?: (row: T, index: number) => string | null;
  getWhatsAppAriaLabel?: (row: T, index: number) => string;
}

export interface DeleteConfirmation<T> {
  title?: string;
  description?: string | ((row: T, index: number) => string);
}

export interface TableProps<T> {
  columns: readonly TableColumn<T>[];
  data: readonly T[];
  getRowKey: (row: T, index: number) => string | number;
  rowActions?: TableRowActions<T>;
  deleteConfirmation?: DeleteConfirmation<T>;
  emptyMessage?: string;
  className?: string;
}

interface TableIconActionProps {
  label: string;
  children: React.ReactElement;
}

function TableIconAction({
  label,
  children,
}: TableIconActionProps): React.ReactElement {
  return <Tooltip label={label}>{children}</Tooltip>;
}

/**
 * Generic typed table for backoffice CRUD views.
 */
export function Table<T>({
  columns,
  data,
  getRowKey,
  rowActions,
  deleteConfirmation,
  emptyMessage = "No hay registros.",
  className,
}: TableProps<T>): React.ReactElement {
  const colCount = columns.length + (rowActions ? 1 : 0);
  const [pendingDelete, setPendingDelete] = useState<{
    row: T;
    index: number;
  } | null>(null);

  const handleDeleteClick = (row: T, index: number): void => {
    setPendingDelete({ row, index });
  };

  const handleConfirmDelete = (): void => {
    if (pendingDelete && rowActions) {
      rowActions.onDelete(pendingDelete.row, pendingDelete.index);
    }
    setPendingDelete(null);
  };

  const resolveDescription = (): string | undefined => {
    if (!deleteConfirmation?.description || !pendingDelete) return undefined;
    const { description } = deleteConfirmation;
    return typeof description === "function"
      ? description(pendingDelete.row, pendingDelete.index)
      : description;
  };

  return (
    <>
      <div className={`table__wrapper${className ? ` ${className}` : ""}`}>
        <table className="table">
          <thead className="table__head">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.id}
                  className={`table__cell table__cell--head${col.className ? ` ${col.className}` : ""}`}
                >
                  {col.header}
                </th>
              ))}
              {rowActions && (
                <th className="table__cell table__cell--head table__cell--actions">
                  Acciones
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr className="table__row">
                <td
                  className="table__cell table__cell--empty"
                  colSpan={colCount}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr key={getRowKey(row, index)} className="table__row">
                  {columns.map((col) => (
                    <td
                      key={col.id}
                      className={`table__cell${col.className ? ` ${col.className}` : ""}`}
                    >
                      {col.cell(row, index)}
                    </td>
                  ))}
                  {rowActions && (
                    <td className="table__cell table__cell--actions">
                      <div className="table__actions">
                        {rowActions.getWhatsAppHref?.(row, index) ? (
                          <TableIconAction
                            label={
                              rowActions.getWhatsAppAriaLabel?.(row, index) ??
                              "Contactar por WhatsApp"
                            }
                          >
                            <a
                              className="table__action-btn"
                              href={
                                rowActions.getWhatsAppHref(row, index) ??
                                undefined
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={
                                rowActions.getWhatsAppAriaLabel?.(row, index) ??
                                "Contactar por WhatsApp"
                              }
                              onClick={(event) => event.stopPropagation()}
                            >
                              <WhatsappIcon aria-hidden="true" />
                            </a>
                          </TableIconAction>
                        ) : null}
                        <TableIconAction label="Editar">
                          <button
                            type="button"
                            className="table__action-btn"
                            aria-label="Editar"
                            onClick={() => rowActions.onEdit(row, index)}
                          >
                            <Pencil aria-hidden="true" />
                          </button>
                        </TableIconAction>
                        <TableIconAction label="Eliminar">
                          <button
                            type="button"
                            className="table__action-btn table__action-btn--critical"
                            aria-label="Eliminar"
                            onClick={() => handleDeleteClick(row, index)}
                          >
                            <Trash2 aria-hidden="true" />
                          </button>
                        </TableIconAction>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {rowActions && (
        <ConfirmDialog
          open={pendingDelete !== null}
          onOpenChange={(open) => {
            if (!open) setPendingDelete(null);
          }}
          title={deleteConfirmation?.title}
          description={resolveDescription()}
          onConfirm={handleConfirmDelete}
          confirmActionTrackSufix="bo_table_delete_confirm"
          cancelActionTrackSufix="bo_table_delete_cancel"
        />
      )}
    </>
  );
}
